// REAL Stories timeline. Every card is built from genuine data — the PWHL
// schedule (regular-season games that actually have a game centre), trades,
// transfers/movements, and contract signings/extensions. Nothing is invented:
// if the data isn't there, no card is made.
//
// - Game days (RS only, must be Final + scored = has a game centre) → the new
//   Team-vs-Team result card AND a multi-slide recap (result → recap → next up).
// - Trades / signings / extensions / movements → newspaper feed cards.
import { TEAM_INFO, allTeamIds, teamColor } from "./teamMap";
import { CONTRACT_SEASON_OPTIONS } from "./contracts";
import { STORY_TEMPLATES } from "./storyTemplates";
import { resolveStory, buildPlayerIndex } from "./storyResolve";
import { applyMagicClinch } from "./standingsRender";

const byTplKey = Object.fromEntries(STORY_TEMPLATES.map((t) => [t.key, t]));
// The 4 shareable formats. Each (template × format) is its OWN entity with its
// own saved Creator override — so the public feed shows every format, and each
// one matches exactly what the owner edited for that template in that format.
const FMT4 = ["v"];   // ONLY 4:5 (vertical) is a public format now — other
// orientations were just reactive reflows, not true dimensions, so we don't emit them.
const fmtsFor = () => ["v"];
// Templates that real data doesn't organically produce — surfaced once on the
// most-recent day (in every supported format, real faces) so the owner can see
// how each one renders publicly and confirm it matches the Creator edit.
const SHOWCASE_KEYS = [
    "game_draft", "game_milestone", "game_record", "game_milestone_prestige", "game_record_prestige", "v1_news", "v1_signing", "v1_resign", "v1_trade", "v3_record_breaker", "v3_milestone", "v1_milestone_marquee", "v1_milestone_number", "v1_milestone_ticket", "v1_milestone_timeline", "v1_milestone_medallion", "game_vs_playoff", "game_vs_preseason", "game_flashback",
    "feed_milestone_watch", "feed_milestone_live", "feed_news", "feed_matchup", "feed_recap",
    "team_gameday", "team_recap", "team_result",
    "announce_partnership", "announce_feature", "announce_update",
];

const col = (id) => teamColor(id);
const tname = (id) => TEAM_INFO[id]?.name || (id || "").toUpperCase();
const tabbr = (id) => TEAM_INFO[id]?.abbr || (id || "").toUpperCase();
const tnick = (id) => (tname(id).split(/\s+/).slice(-1)[0] || "").toUpperCase();
const tnickTitle = (id) => tname(id).split(/\s+/).slice(-1)[0] || "";
const TEAM_SET = new Set(allTeamIds());
const isPwhlTeam = (id) => TEAM_SET.has(id);
const dOnly = (s) => String(s || "").slice(0, 10);
const addDays = (d, n) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
// Full-season label: keep "2025-26" as-is; "2024" → "2024". (User wants the long
// form on contract cards, not the shortened "25-26".)
const fullSeasonLbl = (s) => String(s || "");
// "Signed through" span in full-season form (e.g. "2026-27 → 2028-29"), derived
// from signing season + term length.
const contractSpan = (season, term) => {
    if (!season) return null;
    const sy = parseInt(String(season).slice(0, 4), 10);
    if (!sy) return null;
    const ym = /(\d+)\s*year/i.exec(term || "");
    const years = ym ? parseInt(ym[1], 10) : 1;
    if (!years || years <= 1) return `Through ${fullSeasonLbl(season)}`;
    const ey = sy + years - 1;
    const endSeason = `${ey}-${String((ey + 1) % 100).padStart(2, "0")}`;
    return `${fullSeasonLbl(season)} → ${fullSeasonLbl(endSeason)}`;
};

const TYPE_COLOR = { recap: "#F5C518", matchup: "#38BDF8", trade: "#FB923C", signing: "#FB7185", extension: "#C084FC", movement: "#34D399", news: "#A78BFA" };

// Season a date falls in (PWHL runs ~Nov→May; year labels the calendar start).
// We rely on the explicit season field wherever the source provides it.
function normSeason(season, date) {
    if (season) return season;
    const y = Number(dOnly(date).slice(0, 4));
    return y ? `${y}` : "";
}

export const INT_COL = { USA: "#3C3B6E", CZE: "#D7141A", SUI: "#DA291C", GER: "#FFCE00", AUT: "#ED2939", CAN: "#D80621", FIN: "#003580", SWE: "#006AA7", JPN: "#BC002D", DEN: "#C8102E" };
export function buildRealStories({ schedule = [], trades = [], transfers = [], contracts = [], news = [], players = [], threeStars = [], scoring = [], pxp = [], campInvites = [], goalieLogs = [], history = {}, intl = null } = {}) {
    const byName = {}, byId = {};
    (players || []).forEach((p) => { if (p.name) byName[p.name.toLowerCase()] = p; byId[p.id] = p; });
    const photoFor = (id, name) => (byId[id]?.photo_url) || (byName[(name || "").toLowerCase()]?.photo_url) || null;
    const idFor = (id, name) => byId[id]?.id || byName[(name || "").toLowerCase()]?.id || id || "";

    const newsTitle = {};
    const newsUrl = {};
    (news || []).forEach((n) => {
        if (!n.id) return;
        newsTitle[n.id] = (n.title || "").toUpperCase();
        newsUrl[n.id] = n.external_url || n.url || n.link || n.source_url || "";
    });

    const items = [];
    let seq = 0;
    const push = (o) => { items.push({ id: `real-${seq++}`, typeColor: TYPE_COLOR[o.type] || "#A78BFA", ...o }); };

    // ---------- GAMES (any PLAYED game, all stages/seasons) ----------
    const games = (schedule || [])
        .filter((g) => g.date && g.status === "Final" && g.home_score != null && g.away_score != null)
        .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));

    // Cumulative regular-season record (W-L-OTL) for each team AFTER each game —
    // shown under each crest on the Regular Season result card.
    const recTally = {}, recAfter = {};
    const stageOf = (g) => String(g.stage || "regular").toLowerCase();
    const isPlayoffStage = (st) => st === "playoffs" || st === "playoff" || st === "final";
    const isPreStage = (st) => st === "preseason" || st === "pre";
    games.forEach((g) => {
        if (stageOf(g) !== "regular") return;
        const key = normSeason(g.season, g.date);
        const seasonTally = (recTally[key] = recTally[key] || {});
        const ensure = (id) => (seasonTally[id] = seasonTally[id] || { w: 0, l: 0, otl: 0 });
        const h = ensure(g.home_team_id), a = ensure(g.away_team_id);
        const ot = !!(g.overtime || g.shootout);
        const homeWon = g.home_score > g.away_score;
        const winner = homeWon ? h : a, loser = homeWon ? a : h;
        winner.w++;
        if (ot) loser.otl++; else loser.l++;
        const fmt = (r) => `${r.w}-${r.l}-${r.otl}`;
        recAfter[g.id] = { [g.home_team_id]: fmt(h), [g.away_team_id]: fmt(a) };
    });
    // Cumulative playoff series wins per matchup AFTER each game — drives the pips.
    const poTally = {}, poAfter = {};
    games.forEach((g) => {
        if (!isPlayoffStage(stageOf(g))) return;
        const key = `${normSeason(g.season, g.date)}|${[g.home_team_id, g.away_team_id].sort().join("-")}`;
        const w = (poTally[key] = poTally[key] || {});
        const winId = g.home_score > g.away_score ? g.home_team_id : g.away_team_id;
        w[winId] = (w[winId] || 0) + 1;
        poAfter[g.id] = { homeWins: w[g.home_team_id] || 0, awayWins: w[g.away_team_id] || 0 };
    });

    games.forEach((g) => {
        const home = g.home_team_id, away = g.away_team_id;
        const hs = g.home_score, as = g.away_score;
        const winner = hs >= as ? home : away, loser = hs >= as ? away : home;
        const winCol = col(winner);
        const end = g.shootout ? "SO" : g.overtime ? "OT" : "";
        const status = `FINAL${end ? ` · ${end}` : ""}`;
        const st = stageOf(g);
        const isPlayoff = isPlayoffStage(st), isPre = isPreStage(st);
        const rec = recAfter[g.id] || {};
        const resultStory = {
            type: "recap", teamId: home, oppId: away, teamColor: winCol, pills: ["Final"], status,
            score: { home: hs, away: as }, subject: { kind: "team", team_id: home }, venue: g.venue,
            _gameId: g.id,
        };
        const gSeason = normSeason(g.season, g.date);
        const gStage = g.stage || "regular";
        // Stage-aware LIVE card: preseason & playoff games render on their own
        // series design; regular-season games show a "Regular Season" tag + each
        // team's running record. (No trophies; preseason has no series pips.)
        let vsLayout = "game_vs", vsV1Key = "game_vs_result";
        let vsStory = { ...resultStory, pills: ["Final"], stage: "regular", roundLabel: "Regular Season", records: [rec[home] || "", rec[away] || ""] };
        if (isPlayoff) {
            const po = poAfter[g.id] || {};
            vsLayout = "game_vs_series"; vsV1Key = "game_vs_playoff";
            vsStory = { ...resultStory, stage: "playoffs", pills: ["Final"], series: { round: "Playoffs", homeWins: po.homeWins || 0, awayWins: po.awayWins || 0, bestOf: 5, winsNeeded: 3 } };
        } else if (isPre) {
            vsLayout = "game_vs_series"; vsV1Key = "game_vs_preseason";
            vsStory = { ...resultStory, stage: "preseason", pills: ["Final", "Preseason"], series: { round: "Preseason" } };
        }
        FMT4.forEach((o) => push({ date: g.date, season: gSeason, stage: gStage, _isGame: true, type: "recap",
            sourceRef: { label: `Game data · ${tabbr(away)} @ ${tabbr(home)}`, href: `/game/${g.id}` },
            eventId: `game-${g.id}`, orientation: o, tpl: { key: `game-${g.id}-${o}`, layout: vsLayout, v1Key: vsV1Key }, s: vsStory }));

        // Multi-slide recap (vertical): result → recap headline → next up.
        // Only look for a next game WITHIN THE SAME SEASON — a team's last game of a
        // season must NOT show a "next game" from the following season (no schedule
        // existed for it at the time).
        const nextGame = games.find((x) => x.date > g.date && normSeason(x.season, x.date) === gSeason && (x.home_team_id === winner || x.away_team_id === winner));
        const slides = [
            { layout: "game_vs", v1Key: "game_vs_result", story: resultStory },
            { v1Key: "team_recap", line1: tname(winner), line2: `Beat ${tnick(loser)} ${Math.max(hs, as)}-${Math.min(hs, as)}`, pills: ["Postgame", "Recap"],
                teamId: winner, teamColor: winCol, oppId: loser, subject: { kind: "team", team_id: winner },
                blurb: end
                    ? `${tabbr(winner)} grab two points in ${end === "SO" ? "a shootout" : "overtime"}${g.venue ? ` at ${g.venue}` : ""}, with ${tabbr(loser)} taking one.`
                    : `${tabbr(winner)} take all three points${g.venue ? ` at ${g.venue}` : ""}, closing out ${status.toLowerCase()} over ${tabbr(loser)}.` },
        ];
        if (nextGame) {
            const nh = nextGame.home_team_id, na = nextGame.away_team_id;
            slides.push({ layout: "game_vs", v1Key: "game_vs_pre", story: {
                type: "matchup", teamId: nh, oppId: na, teamColor: col(nh), pills: ["Up Next"],
                when: nextGame.date, venue: nextGame.venue, subject: { kind: "team", team_id: nh } } });
        }
        push({ date: g.date, season: gSeason, stage: gStage, _isGame: true, type: "recap",
            eventId: `game-${g.id}`, orientation: "v", tpl: { key: `slides-${g.id}`, layout: "slides", v1Key: "slides_recap", verticalOnly: true, variant: "dark" },
            s: { teamColor: winCol, teamId: winner, oppId: loser, slides } });
    });

    // ---------- THREE STARS (one per finished game centre) ----------
    // Auto-built from the real three-stars saved on each finished game centre.
    // Resolves each star's player_id to the real player (name + photo).
    const schedById = {};
    (schedule || []).forEach((g) => { if (g.id) schedById[g.id] = g; });
    (threeStars || []).forEach((gc) => {
        const g = schedById[gc.game_id];
        if (!g || !g.home_team_id || !Array.isArray(gc.three_stars) || !gc.three_stars.length) return;
        const date = dOnly(g.date);
        const stars = [1, 2, 3].map((n) => {
            const st = gc.three_stars.find((x) => x.star === n) || gc.three_stars[n - 1];
            if (!st || !st.name) return null;
            const p = byName[(st.name || "").toLowerCase()];
            const stat = st.stat;
            let statLine = "";
            if (stat && stat.kind === "goalie") {
                const pct = stat.svpct == null ? "" : (stat.svpct >= 1 ? "1.000" : `.${String(Math.round(stat.svpct * 1000)).padStart(3, "0")}`);
                statLine = `${stat.sv}/${stat.sa}${pct ? ` ${pct}` : ""}`;
            } else if (stat && stat.kind === "skater") statLine = `${stat.g}G · ${stat.a}A · ${stat.p}P`;
            return { subject: { kind: "player", id: p?.id || idFor(null, st.name), name: st.name, team_id: st.team_id || p?.team_id, photo_url: p?.photo_url || null }, line: tabbr(st.team_id || p?.team_id), statLine };
        }).filter((s) => s && s.subject.name);
        if (!stars.length) return;
        const win = (g.home_score >= g.away_score) ? g.home_team_id : g.away_team_id;
        const matchup = `${tabbr(g.away_team_id)} ${g.away_score} vs ${tabbr(g.home_team_id)} ${g.home_score}`;
        fmtsFor(byTplKey.game_stars3).forEach((o) => push({ date, season: normSeason(g.season, date), stage: g.stage || "regular", _isGame: true, type: "three_stars",
            sourceRef: { label: `Game data · ${tabbr(g.away_team_id)} @ ${tabbr(g.home_team_id)}`, href: `/game/${g.id}` },
            eventId: `stars-${g.id}`, orientation: o, tpl: { key: `stars-${g.id}-${o}`, layout: "game_stars3", v1Key: "game_stars3" },
            s: { type: "three_stars", teamId: win, teamColor: col(win), _homeTeam: g.home_team_id, pills: ["Postgame", "Three Stars"], matchup, stars, _gameId: g.id } }));
    });

    // ---------- STARTING IN NET (per-game starting goalies, REAL data) ----------
    // Source: /goalie-game-logs (starter=true = the goalie who BEGAN the game,
    // taken from that game's roster). For every finished game we emit:
    //   • ONE card per starting goalie (2 per game — home & away starter), and
    //   • ONE "duo" card showing both starters side-by-side.
    // Each card shows the goalie's SEASON record ENTERING that game (W-L-OTL ·
    // SV% · GAA). A goalie's first appearance of the season shows NO stats (they
    // hadn't played yet) — the record only accrues from prior games.
    const svfmt = (p) => (p == null ? "" : p >= 1 ? "1.000" : `.${String(Math.round(p * 1000)).padStart(3, "0")}`);
    const toiSecs = (t) => {
        const m = /(\d+):(\d{1,2})/.exec(String(t || ""));
        return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
    };
    const decBucket = (d) => {
        const u = String(d || "").toUpperCase();
        if (u === "W") return "w";
        if (u === "L") return "l";
        if (u === "OTW" || u === "SOW") return "otw";
        if (u === "OTL" || u === "SOL" || u === "SL") return "otl";
        // Fallbacks for loosely-formatted decisions.
        if (u.includes("OT") || u.includes("SO")) return u.includes("W") ? "otw" : "otl";
        if (u.startsWith("W")) return "w";
        if (u.startsWith("L")) return "l";
        return "";
    };
    // Group starter logs by game id.
    const startersByGame = {};
    (goalieLogs || []).forEach((gl) => {
        if (!gl || !gl.game_id || gl.starter !== true) return;
        (startersByGame[gl.game_id] = startersByGame[gl.game_id] || []).push(gl);
    });
    // Cumulative record per goalie, per season+stage (record resets each season /
    // separates regular vs playoffs).
    const gkTally = {};
    const gkEnter = (key) => {
        const t = (gkTally[key] = gkTally[key] || { w: 0, otw: 0, otl: 0, l: 0, sv: 0, sa: 0, ga: 0, secs: 0, gp: 0 });
        return t;
    };
    const enterLine = (t) => {
        if (!t || t.gp === 0) return { statLine: "", debut: true };
        const rec = `${t.w}-${t.otw}-${t.otl}-${t.l}`;
        const pct = t.sa > 0 ? `${svfmt(t.sv / t.sa)} SV%` : "";
        const gaa = t.secs > 0 ? `${(t.ga * 3600 / t.secs).toFixed(2)} GAA` : "";
        return { statLine: [rec, pct, gaa].filter(Boolean).join(" · "), debut: false };
    };
    // Iterate games in chronological order (already sorted above) so the running
    // record is correct when each game's card is built.
    games.forEach((g) => {
        const gks = startersByGame[g.id];
        if (!gks || !gks.length) return;
        const date = dOnly(g.date);
        const gSeason = normSeason(g.season, date);
        const gStage = g.stage || "regular";
        // Build each starter's card data using the record ENTERING this game,
        // then advance the running tally with this game's result.
        const built = gks.map((gl) => {
            const pid = gl.player_id;
            const key = `${pid}|${gSeason}|${gStage}`;
            const t = gkEnter(key);
            const enter = enterLine(t);
            const p = byId[pid] || byName[(gl.player_name || "").toLowerCase()];
            const name = p?.name || (gl.player_name) || String(pid || "").split("-").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
            const tid = gl.team_id || p?.team_id || "";
            const opp = gl.opponent_team_id || (tid === g.home_team_id ? g.away_team_id : g.home_team_id);
            const card = {
                subject: { kind: "player", id: p?.id || pid, name, team_id: tid, photo_url: p?.photo_url || null },
                number: p?.jersey || "", teamId: tid, oppId: opp, statLine: enter.statLine, debut: enter.debut,
            };
            // Advance the running record with this game's actual result.
            const b = decBucket(gl.decision);
            if (b) t[b] += 1;
            t.sv += Number(gl.saves || 0);
            t.sa += Number(gl.shots_against || 0);
            t.ga += Number(gl.goals_against || 0);
            t.secs += toiSecs(gl.toi || gl.minutes);
            t.gp += 1;
            return card;
        });
        const when = `${tabbr(g.away_team_id)} @ ${tabbr(g.home_team_id)} · ${date}`;
        const srcRef = { label: `Game data · ${tabbr(g.away_team_id)} @ ${tabbr(g.home_team_id)}`, href: `/game/${g.id}` };
        // (a) One card per starting goalie.
        built.forEach((c) => {
            push({ date, season: gSeason, stage: gStage, _isGame: true, type: "news", sourceRef: srcRef,
                eventId: `goalie-${g.id}-${c.subject.id}`, orientation: "v",
                tpl: { key: `goalie-${g.id}-${c.subject.id}-v`, layout: "game_goalie", v1Key: "game_goalie" },
                s: { type: "news", teamId: c.teamId, teamColor: col(c.teamId), oppId: c.oppId, pills: ["Starting In Net"],
                    when, number: c.number, statLine: c.statLine, _debut: c.debut, subject: c.subject, _gameId: g.id } });
        });
        // (b) One duo card (both starters side-by-side) — home team on the left.
        const homeC = built.find((c) => c.teamId === g.home_team_id) || built[0];
        const awayC = built.find((c) => c.teamId === g.away_team_id) || built[1] || built[0];
        if (homeC && awayC) {
            push({ date, season: gSeason, stage: gStage, _isGame: true, type: "news", sourceRef: srcRef,
                eventId: `goalieduo-${g.id}`, orientation: "v",
                tpl: { key: `goalieduo-${g.id}-v`, layout: "game_goalie_duo", v1Key: "game_goalie_duo" },
                s: { type: "news", pills: ["Starting In Net"], when, teamColor: col(g.home_team_id), teamId: g.home_team_id,
                    home: { subject: homeC.subject, teamId: homeC.teamId, statLine: homeC.statLine, debut: homeC.debut, number: homeC.number },
                    away: { subject: awayC.subject, teamId: awayC.teamId, statLine: awayC.statLine, debut: awayC.debut, number: awayC.number },
                    _gameId: g.id } });
        }
    });

    // ---------- HISTORIC STANDINGS (point-in-time, one card per game day) ----------
    // After every active regular-season game day we emit a Standings card showing
    // the table AS OF that date — replayed from the real played games up to and
    // including that day. Nothing invented: only games that actually happened.
    // PWHL points: reg win 3, OT/SO win 2, OT/SO loss 1, reg loss 0.
    const BERTHS = 4;
    const regBySeason = {};
    games.filter((g) => (g.stage || "regular") === "regular" && isPwhlTeam(g.home_team_id) && isPwhlTeam(g.away_team_id))
        .forEach((g) => { const s = normSeason(g.season, g.date); (regBySeason[s] = regBySeason[s] || []).push(g); });
    // Total regular-season games each team is SCHEDULED to play, per season — taken
    // from the full schedule (any status). This drives games-remaining so the Magic #
    // matches the Standings page MAG column exactly (PWHL reg win = 3 pts).
    const totalGamesByTeam = {};
    (schedule || []).forEach((g) => {
        if ((g.stage || "regular") !== "regular") return;
        if (!isPwhlTeam(g.home_team_id) || !isPwhlTeam(g.away_team_id)) return;
        const s = normSeason(g.season, g.date);
        const bucket = (totalGamesByTeam[s] = totalGamesByTeam[s] || {});
        bucket[g.home_team_id] = (bucket[g.home_team_id] || 0) + 1;
        bucket[g.away_team_id] = (bucket[g.away_team_id] || 0) + 1;
    });
    Object.keys(regBySeason).forEach((season) => {
        const seasonGames = regBySeason[season].slice().sort((a, b) => (dOnly(a.date) + (a.time || "")).localeCompare(dOnly(b.date) + (b.time || "")));
        const totalFor = (id) => (totalGamesByTeam[season] && totalGamesByTeam[season][id]) || 0;
        // Gold Plan base points per team — the cumulative points a team held the
        // moment it was FIRST eliminated. Persists across the season's game loop.
        const goldBase = {};
        // PER-GAME standings: after EACH game we emit an updated Standings (+ GD)
        // snapshot reflecting every game up to AND INCLUDING that one. The last game
        // of a day is therefore the end-of-day (daily) table. Each card carries the
        // game id so it can slot into that game's slide group (GROUP = SLIDE).
        seasonGames.forEach((cur, gi) => {
            const gid = cur.id;
            const day = dOnly(cur.date);
            const tally = {};
            const ensure = (id) => (tally[id] = tally[id] || { team_id: id, gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 });
            // Seed EVERY league team (even those yet to play) so the point-in-time
            // table always lists all teams. Without this, game 1 has only the two
            // teams that played → fewer rows than playoff berths → the clinch math's
            // "no chaser" fallback wrongly stamps x-CLINCHED on game one.
            Object.keys(totalGamesByTeam[season] || {}).forEach((id) => ensure(id));
            seasonGames.slice(0, gi + 1).forEach((g) => {
                const h = ensure(g.home_team_id), a = ensure(g.away_team_id);
                h.gp++; a.gp++;
                h.gf += g.home_score; h.ga += g.away_score;
                a.gf += g.away_score; a.ga += g.home_score;
                const ot = !!(g.overtime || g.shootout);
                const winner = g.home_score > g.away_score ? h : a, loser = g.home_score > g.away_score ? a : h;
                if (ot) { winner.otw++; winner.pts += 2; loser.otl++; loser.pts += 1; }
                else { winner.w++; winner.pts += 3; loser.l++; }
            });
            const sorted = Object.values(tally)
                .sort((x, y) => (y.pts - x.pts) || (y.w - x.w) || (y.gf - x.gf))
                .map((r, i) => ({ rank: i + 1, ...r }));
            if (!sorted.length) return;
            // Magic # + clinch (z/x/e) — EXACTLY the Standings-page logic, point-in-time.
            sorted.forEach((r) => { r.rem = Math.max(0, totalFor(r.team_id) - r.gp); });
            applyMagicClinch(sorted, BERTHS);
            // GOLD (Gold Plan) — points a team earns AFTER it is mathematically
            // eliminated. Surfaces only once a team is out; tracked across the season.
            sorted.forEach((r) => {
                if (r.clinch === "e" && goldBase[r.team_id] == null) goldBase[r.team_id] = r.pts;
                r.goldPts = goldBase[r.team_id] != null ? Math.max(0, r.pts - goldBase[r.team_id]) : null;
            });
            const rows = sorted.slice(0, 8);
            const base = { title: "PWHL Standings", stage: "Regular Season", footerLabel: `Standings · ${day}`, teamColor: col(rows[0].team_id), rows, _gameId: gid };
            const src = { label: `Standings as of ${day}`, href: "/standings" };
            fmtsFor(byTplKey.game_standings).forEach((o) => push({ date: day, season, stage: "regular", _isGame: true, type: "recap",
                sourceRef: src, eventId: `standings-${season}-${gid}`, orientation: o,
                tpl: { key: `standings-${season}-${gid}-${o}`, layout: "standings_table", v1Key: "game_standings" }, s: { ...base } }));
            // DAY-RECAP standings: ONE card per day (the end-of-day cumulative
            // table) — a standalone daily card, NOT tied to a single game. Emitted
            // on the LAST game of the day so it reflects every result that day.
            const nextG = seasonGames[gi + 1];
            const isDayEnd = !nextG || dOnly(nextG.date) !== day;
            if (isDayEnd) {
                const dayStory = { title: "PWHL Standings", stage: "Regular Season", footerLabel: `Standings · ${day}`, teamColor: col(rows[0].team_id), rows };
                fmtsFor(byTplKey.game_standings_day).forEach((o) => push({ date: day, season, stage: "regular", _isGame: true, type: "recap",
                    sourceRef: { label: `Standings · end of ${day}`, href: "/standings" }, eventId: `standings-day-${season}-${day}`, orientation: o,
                    tpl: { key: `standings-day-${season}-${day}-${o}`, layout: "standings_table", v1Key: "game_standings_day" }, s: dayStory }));
                // GOAL DIFFERENTIAL — real GD (gf − ga), end-of-day table. DAY-LEVEL
                // (one per day, no _gameId) so it pairs cleanly with the day-recap
                // standings instead of duplicating once per game.
                const gdTeams = sorted.map((r) => ({ id: r.team_id, gd: (r.gf || 0) - (r.ga || 0) })).sort((a, b) => b.gd - a.gd);
                const gdStory = { type: "news", teamColor: col(gdTeams[0].id), kicker: "Goal Differential", subtitle: "GD", seasonLabel: season, source: "PWHL", teams: gdTeams };
                fmtsFor(byTplKey.game_gd).forEach((o) => push({ date: day, season, stage: "regular", _isGame: true, type: "recap",
                    sourceRef: { label: `Goal differential · ${day}`, href: "/standings" }, eventId: `gd-${season}-${day}`, orientation: o,
                    tpl: { key: `gd-${season}-${day}-${o}`, layout: "game_gd", v1Key: "game_gd" }, s: gdStory }));
            }
        });
    });
    // ---------- TODAY'S GAMES → PREVIEW + RECAP (two distinct auto cards) --------
    // PREVIEW auto-publishes at 00:01 on each game day (once the day has arrived)
    // and shows the scheduled slate. RECAP auto-publishes once EVERY game that day
    // has a synced final score, and shows the results. Both are default-ON (v).
    // "Today" is anchored to US EASTERN time (PWHL home timezone), NOT the
    // viewer's local time — so Preview flips at 00:01 ET regardless of country.
    const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const todayByDate = {};
    (schedule || []).forEach((g) => {
        const d = dOnly(g.date);
        if (!d || !g.home_team_id) return;
        (todayByDate[d] = todayByDate[d] || []).push(g);
    });
    Object.keys(todayByDate).forEach((d) => {
        const gs = todayByDate[d];
        const dSeason = normSeason(gs[0].season, d);
        const dStage = gs[0].stage || "regular";
        const arrived = d <= todayStr;                                             // 00:01 of the game day has passed
        const allSynced = gs.every((g) => g.status === "Final" && g.home_score != null);   // game centres synced
        if (arrived) {
            // ONE preview PER GAME (carries _gameId) so it fuses with THAT game's
            // Starting-In-Net duo into a per-game slide deck (never mixes two games).
            gs.forEach((g) => {
                if (!g.id) return;
                fmtsFor(byTplKey.game_today_preview).forEach((o) => push({ date: d, season: dSeason, stage: dStage, _isGame: true, type: "matchup",
                    sourceRef: { label: `Schedule · ${d}`, href: "/season" },
                    eventId: `today-preview-${g.id}`, orientation: o, tpl: { key: `today-preview-${g.id}-${o}`, layout: "game_today_preview", v1Key: "game_today_preview" },
                    s: { _todayDate: d, _todayMode: "team", _todayState: "preview", _onlyGameId: g.id, _gameId: g.id, teamId: g.home_team_id, teamColor: col(g.home_team_id) } }));
            });
        }
        if (allSynced && gs.length > 1) {   // skip the daily recap when only ONE game was played (the single result card already covers it)
            fmtsFor(byTplKey.game_today_recap).forEach((o) => push({ date: d, season: dSeason, stage: dStage, _isGame: true, type: "recap",
                sourceRef: { label: `Results · ${d}`, href: "/season" },
                eventId: `today-recap-${d}`, orientation: o, tpl: { key: `today-recap-${d}-${o}`, layout: "game_today_recap", v1Key: "game_today_recap" },
                s: { _todayDate: d, _todayMode: "stage", _todayState: "recap" } }));
        }
    });

    // ---------- NEXT GAME (per PLAYED game) + END-OF-DAY NEXT-GAMES SLATE ----------
    // Every played game emits its OWN Next Game card, tagged with that game's id
    // so it slots into that game's slide deck (GROUP = SLIDE). The card shows the
    // next scheduled game for the TWO teams that played THIS game (away + home),
    // deduped if they play each other next. The end-of-day slate lists all of the
    // day's teams' next games together, once per day (day-level, its own deck).
    const nextForTeam = (tid, after, season = null) => (schedule || [])
        .filter((g) => (g.home_team_id === tid || g.away_team_id === tid) && dOnly(g.date) > after && (season == null || normSeason(g.season, g.date) === season))
        .sort((a, b) => dOnly(a.date).localeCompare(dOnly(b.date)))[0] || null;
    const dfmt = (x) => new Date(dOnly(x) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const playedByDate = {};
    games.forEach((g) => { const d = dOnly(g.date); (playedByDate[d] = playedByDate[d] || []).push(g); });
    // PER-GAME Next Game: the next scheduled game for each of THIS game's teams.
    games.forEach((g) => {
        const d = dOnly(g.date);
        const gid = g.id;
        const dSeason = normSeason(g.season, d);
        const dStage = g.stage || "regular";
        const seen = new Set(); const rows = [];
        [g.away_team_id, g.home_team_id].filter(isPwhlTeam).forEach((tid) => {
            const nx = nextForTeam(tid, d, dSeason);
            if (!nx || seen.has(nx.id)) return;   // if both teams' next game is the same, list once
            seen.add(nx.id);
            rows.push({ a: nx.away_team_id, h: nx.home_team_id, date: dfmt(nx.date), _raw: dOnly(nx.date) });
        });
        if (!rows.length) return;
        // Chronological, matching the schedule page (Nov 30 above Dec 1).
        rows.sort((r1, r2) => String(r1._raw).localeCompare(String(r2._raw)));
        fmtsFor(byTplKey.game_next).forEach((o) => push({ date: d, season: dSeason, stage: dStage, _isGame: true, type: "matchup",
            sourceRef: { label: `Next game · ${tabbr(g.away_team_id)} @ ${tabbr(g.home_team_id)}`, href: "/season" },
            eventId: `next-${gid}`, orientation: o, tpl: { key: `next-${gid}-${o}`, layout: "game_next", v1Key: "game_next" },
            s: { _nextRows: rows, _gameId: gid, teamColor: col(g.home_team_id) } }));
    });
    // END-OF-DAY SLATE: one card per day listing every played team's next game.
    Object.keys(playedByDate).forEach((d) => {
        const dayGames = playedByDate[d];
        const daySeason = normSeason(dayGames[0].season, d);
        const teams = Array.from(new Set(dayGames.flatMap((g) => [g.away_team_id, g.home_team_id]).filter(isPwhlTeam)));
        const seen = new Set(); const matchups = [];
        teams.forEach((tid) => {
            const nx = nextForTeam(tid, d, daySeason);
            if (!nx || seen.has(nx.id)) return;   // dedup: same next game (rematch) only once
            seen.add(nx.id);
            matchups.push({ a: nx.away_team_id, h: nx.home_team_id, date: dfmt(nx.date), _raw: dOnly(nx.date) });
        });
        if (!matchups.length) return;
        // Chronological, matching the schedule page (Nov 30 above Dec 1).
        matchups.sort((m1, m2) => String(m1._raw).localeCompare(String(m2._raw)));
        const dSeason = normSeason(dayGames[0].season, d);
        const dStage = dayGames[0].stage || "regular";
        fmtsFor(byTplKey.game_next_slate).forEach((o) => push({ date: d, season: dSeason, stage: dStage, _isGame: true, type: "matchup",
            sourceRef: { label: `Next games · ${d}`, href: "/season" },
            eventId: `next-slate-${d}`, orientation: o, tpl: { key: `next-slate-${d}-${o}`, layout: "game_next_slate", v1Key: "game_next_slate" },
            s: { _slate: matchups, source: "PWHL", teamColor: col(matchups[0].h) } }));
    });


    // ---------- BIRTHDAYS (real PWHL players) ----------
    // Only surface birthdays in the PREVIOUS, CURRENT or UPCOMING month (relative to
    // today in US Eastern), each dated to the correct year — so a card shows only
    // when its month is inside that rolling 3-month window. 2+ on a day → a slide.
    const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const bWindows = [-1, 0, 1].map((off) => {
        const d = new Date(nowET.getFullYear(), nowET.getMonth() + off, 1);
        return { y: d.getFullYear(), m: d.getMonth() + 1 };   // m = 1..12
    });
    const roster = (players || []).filter((p) => p.birthdate && isPwhlTeam(p.team_id) && p.active !== false && (!p.league || p.league === "PWHL"));
    const bday = {};
    roster.forEach((p) => {
        const bm = Number(dOnly(p.birthdate).slice(5, 7));   // birth month 1..12
        const dd = dOnly(p.birthdate).slice(8, 10);
        bWindows.forEach(({ y, m }) => {
            if (bm !== m) return;
            const key = `${y}-${String(m).padStart(2, "0")}-${dd}`;
            (bday[key] = bday[key] || []).push(p);
        });
    });
    const ageOn = (p, dateStr) => { const by = Number(dOnly(p.birthdate).slice(0, 4)); const yr = Number(dateStr.slice(0, 4)); return by && yr ? yr - by : null; };
    const bdayStory = (p, dateStr) => ({ pills: ["Happy Birthday"], dateLabel: new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }), age: ageOn(p, dateStr), subject: { kind: "player", id: p.id, name: p.name, team_id: p.team_id, photo_url: p.photo_url || null } });
    // ALWAYS emit STANDALONE birthday cards — one per player. The Creator's
    // Solo|Slide toggle (multiMode "slide") fuses same-day birthdays into ONE
    // deck; Solo (default) keeps each birthday as its own card.
    Object.entries(bday).forEach(([d, ppl]) => {
        ppl.forEach((p) => {
            fmtsFor(byTplKey.misc_birthday).forEach((o) => push({ date: d, season: normSeason(null, d), stage: "regular", type: "news",
                sourceRef: { label: "Birthday", href: `/players/${p.id}` }, eventId: `bday-${d}-${p.id}`, orientation: o,
                tpl: { key: `bday-${d}-${p.id}-${o}`, layout: "misc_birthday", v1Key: "misc_birthday" }, s: bdayStory(p, d) }));
        });
    });

    // ---------- TRADES (PWHL, real) ----------
    (trades || []).filter((t) => t.date && isPwhlTeam(t.team_a_id) && isPwhlTeam(t.team_b_id)).forEach((t) => {
        const date = dOnly(t.date);
        const aPlayers = (t.team_a_receives || []).filter((r) => r.kind === "player" && r.player_name);
        const bPlayers = (t.team_b_receives || []).filter((r) => r.kind === "player" && r.player_name);
        // Present from the side that actually receives a player.
        const side = aPlayers.length ? { team: t.team_a_id, from: t.team_b_id, p: aPlayers[0], gets: aPlayers, oppGets: bPlayers }
            : bPlayers.length ? { team: t.team_b_id, from: t.team_a_id, p: bPlayers[0], gets: bPlayers, oppGets: aPlayers } : null;
        if (!side) return;
        const tSeason = normSeason(t.season, date);
        const eid = `trade-${date}-${side.p.player_id}`;
        const feedStory = { type: "trade", typeColor: TYPE_COLOR.trade, teamId: side.team, teamColor: col(side.team), oppId: side.from, ago: date, pills: ["Trade"],
            subject: { kind: "player", id: idFor(side.p.player_id, side.p.player_name), name: side.p.player_name, photo_url: photoFor(side.p.player_id, side.p.player_name), team_id: side.team },
            line1: `${tnick(side.team)} Acquire`, line2: side.p.player_name,
            blurb: `${tabbr(side.team)} acquire ${side.p.position || ""} ${side.p.player_name} from ${tabbr(side.from)} in a trade.` };
        fmtsFor(byTplKey.feed_trade).forEach((o) => push({ date, season: tSeason, stage: "regular", type: "trade", eventId: eid, orientation: o,
            tpl: { key: `${eid}-ft-${o}`, layout: "feedcard", feedCard: true, v1Key: "feed_trade" }, s: feedStory }));
        // Real trade ALSO rendered as the dedicated Trade template.
        const gtStory = { type: "trade", teamId: side.team, teamColor: col(side.team), oppId: side.from, pills: ["Trade"], when: date,
            aGets: (side.gets || []).map((r) => r.player_name).filter(Boolean),
            bGets: ((side.oppGets || []).map((r) => r.player_name).filter(Boolean).length ? side.oppGets.map((r) => r.player_name).filter(Boolean) : ["Future Considerations"]) };
        fmtsFor(byTplKey.game_trade).forEach((o) => push({ date, season: tSeason, stage: "regular", type: "trade", eventId: `${eid}-gt`, orientation: o,
            tpl: { key: `${eid}-gt-${o}`, layout: "game_trade", v1Key: "game_trade" }, s: gtStory }));
    });

    // ---------- SIGNINGS / EXTENSIONS (contract history) ----------
    const contractsSorted = (contracts || []).filter((c) => c.date_signed && isPwhlTeam(c.team_id) && c.player_name)
        .sort((a, b) => dOnly(b.date_signed).localeCompare(dOnly(a.date_signed)));
    // Re-sign detection: a contract for a player who already had an EARLIER
    // contract with the SAME team (data has no explicit type field).
    const pkey = (c) => `${c.player_id ? `id:${c.player_id}` : `nm:${(c.player_name || "").toLowerCase()}`}|${c.team_id}`;
    const datesByKey = {};
    (contracts || []).forEach((c) => { if (c.date_signed && c.player_name) (datesByKey[pkey(c)] = datesByKey[pkey(c)] || []).push(dOnly(c.date_signed)); });
    const hasPrior = (c) => (datesByKey[pkey(c)] || []).some((x) => x < dOnly(c.date_signed));
    contractsSorted.forEach((c) => {
        const date = dOnly(c.date_signed);
        const title = (newsTitle[c.source_news_id] || "").toUpperCase();
        const isExt = /EXTEND/.test(title);
        const isResign = !isExt && (/RE-?SIGN|RESIGN/.test(title) || hasPrior(c));
        const type = isExt ? "extension" : isResign ? "resign" : "signing";
        const label = isExt ? "Extension" : isResign ? "Re-sign" : "Signing";
        const verbT = isExt ? "Extend" : isResign ? "Re-sign" : "Sign";
        const verb = isExt ? "extend" : isResign ? "re-sign" : "sign";
        const cSeason = normSeason(c.season, date);
        const player = byId[c.player_id] || byName[(c.player_name || "").toLowerCase()];
        // Prefer the player's MERGED contract run for the accurate END season
        // (a single history row's term can be stale/short). Falls back to term.
        const toReal = (s) => { s = String(s || "").trim(); if (/^\d{4}$/.test(s) && !CONTRACT_SEASON_OPTIONS.includes(s)) { const y = Number(s); return `${y}-${String((y + 1) % 100).padStart(2, "0")}`; } return s; };
        let span = contractSpan(c.season, c.term);
        if (player) {
            const startReal = toReal(c.season);
            const row = (player.contracts || []).find((r) => r.season === startReal && r.team_id === c.team_id && r.contract_years);
            const idx = CONTRACT_SEASON_OPTIONS.indexOf(startReal);
            if (row && row.contract_years && idx >= 0) {
                const startIdx = idx - ((row.contract_year || 1) - 1);
                const endSeason = CONTRACT_SEASON_OPTIONS[startIdx + row.contract_years - 1];
                if (endSeason) span = row.contract_years <= 1 ? `Through ${fullSeasonLbl(endSeason)}` : `${fullSeasonLbl(CONTRACT_SEASON_OPTIONS[startIdx])} → ${fullSeasonLbl(endSeason)}`;
            }
        }
        const srcUrl = newsUrl[c.source_news_id];
        const cStory = { type, typeColor: TYPE_COLOR[type] || TYPE_COLOR.extension, teamId: c.team_id, teamColor: col(c.team_id), ago: date, pills: [label, c.term, span].filter(Boolean), _line1Case: "caps",
            subject: { kind: "player", id: idFor(c.player_id, c.player_name), name: c.player_name, photo_url: c.photo_url || photoFor(c.player_id, c.player_name), team_id: c.team_id },
            line1: `${tnickTitle(c.team_id)} ${verbT}`, line2: c.player_name,
            blurb: `${tname(c.team_id)} ${verb} ${c.position || ""} ${c.player_name}${c.term ? ` — ${c.term}` : ""}${span ? ` (${span})` : ""}.` };
        const cKey = isExt ? "feed_contract_extended" : isResign ? "feed_contract_resigned" : "feed_contract_signed";
        fmtsFor(byTplKey[cKey]).forEach((o) => push({ date, season: cSeason, stage: "regular", type, eventId: `contract-${c.id}`, orientation: o,
            sourceRef: srcUrl ? { label: `News source · ${c.player_name}`, href: srcUrl } : { label: `Contract history · ${c.player_name}`, href: "/contracts" },
            tpl: { key: `contract-${c.id}-${o}`, layout: "feedcard", feedCard: true, v1Key: cKey }, s: cStory }));
    });

    // ---------- CAMP INVITES (offseason training-camp invites, owner-dated) ----------
    (campInvites || []).filter((ci) => ci.date && ci.player_name && isPwhlTeam(ci.team_id))
        .sort((a, b) => dOnly(b.date).localeCompare(dOnly(a.date))).forEach((ci) => {
        const date = dOnly(ci.date);
        const ciSeason = normSeason(ci.season, date);
        const ciStory = { type: "signing", typeColor: TYPE_COLOR.signing || "#A78BFA", teamId: ci.team_id, teamColor: col(ci.team_id), ago: date, pills: ["Camp Invite", tabbr(ci.team_id)], _line1Case: "caps",
            subject: { kind: "player", id: idFor(ci.player_id, ci.player_name), name: ci.player_name, photo_url: ci.photo_url || photoFor(ci.player_id, ci.player_name), team_id: ci.team_id },
            line1: `${tnickTitle(ci.team_id)} Camp Invite`, line2: ci.player_name,
            blurb: `${ci.player_name} joins ${tname(ci.team_id)} training camp.` };
        fmtsFor(byTplKey.feed_camp_invite).forEach((o) => push({ date, season: ciSeason, stage: "regular", type: "signing", eventId: `camp-${ci.id}`, orientation: o,
            sourceRef: { label: `Camp Invite · ${ci.player_name}`, href: "/contracts" },
            tpl: { key: `camp-${ci.id}-${o}`, layout: "feedcard", feedCard: true, v1Key: "feed_camp_invite" }, s: ciStory }));
    });

    // ---------- MOVEMENTS (PWHL team → PWHL team transfers) ----------
    (transfers || []).filter((t) => t.league === "PWHL" && t.type === "Transfer" && t.date && isPwhlTeam(t.to_team_id) && t.player_name)
        .sort((a, b) => dOnly(b.date).localeCompare(dOnly(a.date))).slice(0, 120).forEach((t) => {
        const date = dOnly(t.date);
        const mSeason = normSeason(t.season, date);
        const mStory = { type: "trade", typeColor: TYPE_COLOR.movement, teamId: t.to_team_id, teamColor: col(t.to_team_id), oppId: isPwhlTeam(t.from_team_id) ? t.from_team_id : null, ago: date, pills: ["Roster Move"],
            subject: { kind: "player", id: idFor(t.player_id, t.player_name), name: t.player_name, photo_url: t.photo_url || photoFor(t.player_id, t.player_name), team_id: t.to_team_id },
            line1: `${tnick(t.to_team_id)} Add`, line2: t.player_name,
            blurb: `${t.player_name} joins ${tname(t.to_team_id)}${isPwhlTeam(t.from_team_id) ? ` from ${tabbr(t.from_team_id)}` : ""}.` };
        fmtsFor(byTplKey.feed_trade).forEach((o) => push({ date, season: mSeason, stage: "regular", type: "movement", eventId: `move-${date}-${t.player_id}`, orientation: o,
            tpl: { key: `move-${date}-${t.player_id}-${o}`, layout: "feedcard", feedCard: true, v1Key: "feed_trade" }, s: mStory }));
    });

    // ---------- HISTORY page items → one story each (skips anything already told: same person + same day) ----------
    const told = new Set(items.map((it) => `${String(it.s?.subject?.name || "").toLowerCase()}|${it.date}`));
    const H_COLOR = { injury: "#F87171", discipline: "#FBBF24", staff: "#60A5FA", roster: "#34D399", absence: "#94A3B8", move: TYPE_COLOR.movement, champ: "#F5C518" };
    const histAdd = (kind, h, i, { pill, line1, blurb }) => {
        const date = dOnly(h.date);
        const team = h.to_team_id || h.from_team_id || h.team_id;
        if (!date || !h.player_name || (h.league && h.league !== "PWHL") || (team && !isPwhlTeam(team))) return;
        const key = `${h.player_name.toLowerCase()}|${date}`;
        if (told.has(key)) return;
        told.add(key);
        const s = { _forceStory: true, type: "news", typeColor: H_COLOR[kind], teamId: team, teamColor: col(team), ago: date, pills: [pill],
            subject: { kind: "player", id: idFor(h.player_id, h.player_name), name: h.player_name, photo_url: h.photo_url || photoFor(h.player_id, h.player_name), team_id: team },
            line1, line2: h.player_name, blurb };
        push({ date, season: normSeason(h.season, date), stage: "regular", type: "news", eventId: `hist-${kind}-${date}-${h.player_id || h.player_name}-${i}`, orientation: "v",
            sourceRef: { label: "History", href: "/transfers" },
            tpl: { key: `hist-${kind}-${date}-${i}-v`, layout: "feedcard", feedCard: true, v1Key: "feed_trade" }, s });
    };
    const note = (h) => (h.note ? ` — ${h.note}` : "");
    (history.transfers || []).filter((t) => t.type && t.type !== "Transfer").forEach((t, i) => histAdd("move", t, i, { pill: t.type, line1: `${tnick(t.to_team_id || t.from_team_id)} ${t.type}`, blurb: `${t.player_name}: ${t.type}${note(t)}.` }));
    (history.injuries || []).forEach((h, i) => histAdd("injury", h, i, { pill: "Injury", line1: `${tnick(h.to_team_id)} Injury Update`, blurb: `${h.player_name} — ${h.type || "Injury"}${h.injury ? ` (${h.injury})` : ""}${note(h)}.` }));
    (history.discipline || []).forEach((h, i) => histAdd("discipline", h, i, { pill: "Discipline", line1: `${tnick(h.to_team_id)} Discipline`, blurb: `${h.player_name} — ${h.type}${h.discipline ? ` · ${h.discipline}` : ""}${note(h)}.` }));
    (history.staff || []).forEach((h, i) => histAdd("staff", h, i, { pill: "Staff", line1: `${tnick(h.to_team_id || h.from_team_id)} ${h.type || "Staff Move"}`, blurb: `${h.player_name}${note(h)}.` }));
    (history.roster || []).forEach((h, i) => histAdd("roster", h, i, { pill: String(h.type || "").startsWith("Jersey") ? "Jersey" : "Captaincy", line1: `${tnick(h.to_team_id)} ${h.type || "Roster Change"}`, blurb: `${h.player_name}${note(h)}.` }));
    (history.absences || []).forEach((h, i) => histAdd("absence", h, i, { pill: "Availability", line1: `${tnick(h.to_team_id)} ${h.type || "Out"}`, blurb: `${h.player_name} missed ${h.games || 0} game${h.games === 1 ? "" : "s"}${h.end_date && h.end_date !== h.date ? ` (${dOnly(h.date)} – ${dOnly(h.end_date)})` : ""}.` }));

    // ---------- PLAY-BY-PLAY → real goal cards (the LIVE PBP templates) ----------
    // For the handful of games with real goal-by-goal data, emit ONE Play-by-Play
    // card per goal, in order, exactly matching the game record (period · time ·
    // scorer · assists · strength · running score). Our data holds GOALS only.
    const prettyName = (slug) => String(slug || "").split("-").map((w) => w ? w[0].toUpperCase() + w.slice(1) : "").join(" ").trim();
    // Chronological sort key for PBP events: period-major, then seconds-into-period.
    const pbpOrder = (period, time, secs) => {
        const p = Number(period) || 1;
        let s = Number(secs);
        if (!Number.isFinite(s)) {
            const m = String(time || "").match(/(\d+):(\d+)/);
            s = m ? Number(m[1]) * 60 + Number(m[2]) : 0;
        }
        return p * 100000 + s;
    };
    (scoring || []).forEach((g) => {
        const date = dOnly(g.date);
        const season = normSeason(g.season, g.date);
        const stage = g.stage || "regular";
        const home = g.home_team_id, away = g.away_team_id;
        let hs = 0, as = 0;
        (g.scoring || []).forEach((ev, gi) => {
            if (ev.team_id === home) hs += 1; else if (ev.team_id === away) as += 1;
            const sc = byId[ev.scorer_id];
            const scorerName = sc?.name || prettyName(ev.scorer_id);
            const a1 = ev.assist1_id ? (byId[ev.assist1_id]?.name || prettyName(ev.assist1_id)) : "";
            const a2 = ev.assist2_id ? (byId[ev.assist2_id]?.name || prettyName(ev.assist2_id)) : "";
            const assists = [a1, a2].filter(Boolean).join(", ");
            const strength = ev.strength && ev.strength !== "EV" ? ev.strength : "";
            const tags = [strength, ev.empty_net ? "EN" : "", ev.game_winning ? "GWG" : ""].filter(Boolean).join(" · ");
            const running = `${tabbr(home)} ${hs}–${as} ${tabbr(away)}`;
            const detail = `${assists ? `Assists: ${assists}` : "Unassisted"}${tags ? ` · ${tags}` : ""} · ${running}`;
            const subject = { kind: "player", id: sc?.id || ev.scorer_id, name: scorerName, photo_url: sc?.photo_url || photoFor(ev.scorer_id, scorerName) || null };
            const gcHref = `/game/${g.game_id}`;
            const src = { label: `Play-by-play · ${tabbr(away)} @ ${tabbr(home)}`, href: gcHref };
            const base = { date, season, stage, _isGame: true, type: "recap", sourceRef: src, orientation: "v" };
            const pbpSeq = pbpOrder(ev.period, ev.time);
            const story = { _pbpKind: "goal", _pbpSeq: pbpSeq, _gameId: g.game_id, moreHref: gcHref, teamId: ev.team_id, teamColor: col(ev.team_id),
                pills: ["Goal", `P${ev.period}`], period: ev.period, time: ev.time, label: "Goal", subject, detail };
            // Spotlight + Ticker are BOTH live templates for the same goal event.
            push({ ...base, eventId: `pbp-spot-${g.game_id}-${gi}`,
                tpl: { key: `pbp-spot-${g.game_id}-${gi}-v`, layout: "pbp_card", v1Key: "pbp_goal_spot" },
                s: { ...story, _pbpVariant: "spotlight" } });
            push({ ...base, eventId: `pbp-tick-${g.game_id}-${gi}`,
                tpl: { key: `pbp-tick-${g.game_id}-${gi}-v`, layout: "pbp_card", v1Key: "pbp_goal_ticker" },
                s: { ...story, _pbpVariant: "ticker" } });
        });
    });

    // ---------- PLAY-BY-PLAY → shot / penalty / faceoff / goalie cards ----------
    // Emit ALL event types; each template's per-surface toggle (isItemHidden → S)
    // decides whether it reaches the public timeline. Penalty is a SPOTLIGHT card
    // (always red); the rest are compact tickers. Full PBP also renders on the
    // Game Centre (gated by the GC surface there).
    // Every PBP event is now a compact TICKER (Play-by-Play group). Shootout ALSO
    // emits a SOGW spotlight for the game-winning attempt (kept in the Game Recap
    // group). Penalty/Shootout are tickers here — the standalone Goal + SOGW
    // spotlights live in the Game Recap group (goal spot emitted above).
    const PBP_PUB = {
        penalty: [{ v1Key: "pbp_penalty_ticker", label: "Penalty", variant: "ticker", kind: "penalty" }],
        shot: [{ v1Key: "pbp_shot_ticker", label: "Shot on Goal", variant: "ticker", kind: "shot" }],
        faceoff: [{ v1Key: "pbp_faceoff_ticker", label: "Faceoff Won", variant: "ticker", kind: "faceoff" }],
        goalie_change: [{ v1Key: "pbp_goalie_ticker", label: "Goaltender", variant: "ticker", kind: "goalie" }],
        hit: [{ v1Key: "pbp_hit_ticker", label: "Hit", variant: "ticker", kind: "hit" }],
        blocked_shot: [{ v1Key: "pbp_block_ticker", label: "Blocked Shot", variant: "ticker", kind: "block" }],
        shootout: [
            { v1Key: "pbp_shootout_ticker", label: "Shootout", variant: "ticker", kind: "shootout" },
            { v1Key: "pbp_shootout_spot", label: "Shootout", variant: "spotlight", kind: "shootout", gwgOnly: true },
        ],
    };
    const jn = (j, name) => (j && j !== "0" ? `#${j} ${name}` : name);
    (pxp || []).forEach((g) => {
        const date = dOnly(g.date);
        const season = normSeason(g.season, g.date);
        const stage = g.stage || "regular";
        const gcHref = `/game/${g.game_id}`;
        const src = { label: `Play-by-play · ${tabbr(g.away_team_id)} @ ${tabbr(g.home_team_id)}`, href: gcHref };
        (g.events || []).forEach((ev, gi) => {
            const cfgs = PBP_PUB[ev.type];
            if (!cfgs || !cfgs.length) return;
            const nm = ev.name || prettyName(ev.slug);
            // Reworded, Rosterly-styled PBP lines per event kind — every ticker
            // shows a full sentence, not just the player's name.
            let subject = { kind: "player", id: ev.slug || nm, name: jn(ev.jersey, nm), photo_url: photoFor(ev.slug, nm) || null };
            let detail = ev.detail;
            if (ev.type === "hit") {
                detail = `${jn(ev.jersey, nm)} laid a hit`;
            } else if (ev.type === "shot") {
                detail = `${jn(ev.jersey, nm)} fired a shot on goal`;
            } else if (ev.type === "faceoff") {
                detail = `${jn(ev.jersey, nm)} won the faceoff`;
            } else if (ev.type === "blocked_shot") {
                // Credit the BLOCKER (owns the stat); name the shooter in the line.
                const bl = ev.blocker_name || nm;
                subject = { kind: "player", id: ev.blocker_slug || bl, name: jn(ev.blocker_jersey, bl), photo_url: photoFor(ev.blocker_slug, bl) || null };
                detail = `${jn(ev.blocker_jersey, bl)} blocked ${jn(ev.jersey, nm)}'s shot`;
            } else if (ev.type === "shootout") {
                const g_ = ev.goalie_name ? `on ${jn(ev.goalie_jersey, ev.goalie_name)}` : "";
                detail = `Shootout attempt ${g_} — ${ev.is_goal ? "GOAL" : "NO GOAL"}${ev.is_gwg ? " · GAME WINNING" : ""}`.replace("  ", " ").trim();
            }
            cfgs.forEach((cfg) => {
                if (cfg.gwgOnly && !ev.is_gwg) return;
                push({ date, season, stage, _isGame: true, type: "recap", sourceRef: src, orientation: "v",
                    eventId: `pbp-${cfg.v1Key}-${g.game_id}-${gi}`,
                    tpl: { key: `pbp-${cfg.v1Key}-${g.game_id}-${gi}-v`, layout: "pbp_card", v1Key: cfg.v1Key },
                    s: { _pbpKind: cfg.kind, _pbpVariant: cfg.variant, _pbpSeq: pbpOrder(ev.period, ev.time, ev.secs), _gameId: g.game_id,
                         _forceStory: cfg.variant === "spotlight" && ev.type === "shootout" && !!ev.is_gwg,
                         moreHref: gcHref, teamId: ev.team_id, teamColor: col(ev.team_id),
                         period: ev.period, time: ev.time, label: cfg.label, subject, detail } });
            });
        });
    });

    // ---------- INTERNATIONAL (IIHF Women's World Championship) ----------
    const intlGames = (intl?.games || []).filter((g) => g.start_utc);
    const intAdd = (date, key, s) => push({ date, season: normSeason(null, date), stage: "regular", type: "news", league: "INT", eventId: `int-${key}`, orientation: "v",
        sourceRef: { label: "WWHC", href: "/nat/schedule" }, tpl: { key: `int-${key}-v`, layout: "feedcard", feedCard: true, v1Key: "feed_trade" },
        s: { _forceStory: true, type: "news", typeColor: "#E11D48", ago: date, ...s } });
    if (intlGames.length) {
        const first = intlGames.map((g) => new Date(g.start_utc).toLocaleDateString("en-CA")).sort()[0];
        intAdd(first, "preview", { teamColor: "#E11D48", pills: ["Women's Worlds"], line1: "IIHF Women's World Championship", line2: "Herning & Esbjerg",
            blurb: `10 nations, Nov 6–16 in Denmark. Group A: USA, Czechia, Switzerland, Germany, Austria. Group B: Canada, Finland, Sweden, Japan, Denmark.` });
    }
    // Day boards: ONE story 30 min before the first game (Today's Games + Standings) and ONE once the
    // last game ends (Today's Results + Standings) — each with 2 slides.
    const intByDay = {};
    intlGames.forEach((g) => { const d = new Date(g.start_utc).toLocaleDateString("en-CA"); (intByDay[d] = intByDay[d] || []).push(g); });
    const intTeams = {};
    intlGames.forEach((g) => [g.away, g.home].forEach((t) => { if (t?.code) intTeams[t.code] = { code: t.code, iso: t.iso, group: g.group || intTeams[t.code]?.group }; }));
    const intTable = (upto, inclusive) => {
        const tb = {};
        Object.values(intTeams).filter((t) => t.group).forEach((t) => { tb[t.code] = { ...t, pts: 0, gp: 0 }; });
        intlGames.filter((g) => g.state === "FINAL" && g.group && (inclusive ? new Date(g.start_utc).toLocaleDateString("en-CA") <= upto : new Date(g.start_utc).toLocaleDateString("en-CA") < upto)).forEach((g) => {
            const a = tb[g.away?.code], h = tb[g.home?.code];
            if (!a || !h || g.away.score == null) return;
            const [w, l] = g.away.score > g.home.score ? [a, h] : [h, a];
            w.gp++; l.gp++; w.pts += g.decision ? 2 : 3; l.pts += g.decision ? 1 : 0;
        });
        return ["A", "B"].map((grp) => ({ label: `Group ${grp}`, rows: Object.values(tb).filter((t) => t.group === grp).sort((x, y) => y.pts - x.pts || x.code.localeCompare(y.code)).map((t, i) => ({ rank: i + 1, code: t.code, iso: t.iso, right: `${t.pts} PTS · ${t.gp} GP` })) })).filter((g) => g.rows.length);
    };
    const nowMs = Date.now();
    const intBoardX = (title, groups, stage, date) => ({ layout: "int_board", v1Key: "int_board", story: { title, groups, stage, teamColor: "#E11D48", footerLabel: `Women's Worlds · ${date}` } });
    if (intlGames.length) {
        const first = intlGames.map((g) => new Date(g.start_utc).toLocaleDateString("en-CA")).sort()[0];
        const d = first;
        push({ date: d, season: normSeason(null, d), stage: "regular", type: "news", league: "INT", eventId: "int-groups-pre", orientation: "v",
            sourceRef: { label: "WWHC", href: "/nat/standings" }, tpl: { key: "int-groups-pre", layout: "slides", verticalOnly: true, variant: "dark" },
            s: { _forceStory: true, teamColor: "#E11D48", slides: [intBoardX("Group Standings", intTable(first, false), "Before the tournament", d)] } });
    }
    const intBoard = (date, key, title, groups, stage) => ({ layout: "int_board", v1Key: "int_board", story: { title, groups, stage, teamColor: "#E11D48", footerLabel: `Women's Worlds · ${date}` } });
    Object.entries(intByDay).forEach(([date, games]) => {
        const sorted = games.slice().sort((x, y) => new Date(x.start_utc) - new Date(y.start_utc));
        const first = new Date(sorted[0].start_utc).getTime();
        const row = (g, right) => ({ code: g.away.code, iso: g.away.iso, vs: { code: g.home.code, iso: g.home.iso }, right });
        if (nowMs >= first - 30 * 60 * 1000) {
            const list = sorted.map((g) => row(g, new Date(g.start_utc).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })));
            push({ date, season: normSeason(null, date), stage: "regular", type: "news", league: "INT", eventId: `int-day-pre-${date}`, orientation: "v",
                sourceRef: { label: "WWHC", href: "/nat/schedule" }, tpl: { key: `int-day-pre-${date}`, layout: "slides", verticalOnly: true, variant: "dark" },
                s: { _forceStory: true, teamColor: "#E11D48", slides: [intBoard(date, "games", "Today's Games", [{ rows: list }], "Game Day"), intBoard(date, "table", "Today's Standings", intTable(date, false), "Before puck drop")] } });
        }
        if (sorted.every((g) => g.state === "FINAL")) {
            const list = sorted.map((g) => ({ ...row(g, g.decision ? `Final/${g.decision}` : "Final"), vsLabel: `${g.away.score}–${g.home.score}` }));
            push({ date, season: normSeason(null, date), stage: "regular", type: "news", league: "INT", eventId: `int-day-post-${date}`, orientation: "v",
                sourceRef: { label: "WWHC", href: "/nat/schedule" }, tpl: { key: `int-day-post-${date}`, layout: "slides", verticalOnly: true, variant: "dark" },
                s: { _forceStory: true, teamColor: "#E11D48", slides: [intBoard(date, "results", "Today's Results", [{ rows: list }], "Final"), intBoard(date, "table", "Today's Standings", intTable(date, true), "After tonight")] } });
        }
    });

    // ---------- INTERNATIONAL END ----------
    intlGames.forEach((g) => {
        const date = new Date(g.start_utc).toLocaleDateString("en-CA");
        const a = g.away || {}, h = g.home || {};
        if (!a.iso || !h.iso) return;
        const where = String(g.venue || "").replace("Hockey Arena ", "");
        const stage = `${g.phase}${g.group ? ` · Group ${g.group}` : ""}`;
        if (g.state === "FINAL" && a.score != null && h.score != null) {
            const win = a.score > h.score ? a : h, lose = win === a ? h : a;
            const end = g.decision ? ` (${g.decision})` : "";
            intAdd(date, `final-${g.id}`, { teamColor: INT_COL[win.code] || "#E11D48", pills: ["Final", "Women's Worlds"], line1: stage,
                line2: `${a.code} ${a.score}–${h.score} ${h.code}`, blurb: `${win.name} beat ${lose.name} ${Math.max(a.score, h.score)}–${Math.min(a.score, h.score)}${end} in ${where}.` });
        } else {
            const t = new Date(g.start_utc).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
            intAdd(date, `game-${g.id}`, { teamColor: INT_COL[h.code] || "#E11D48", pills: ["Game Day", "Women's Worlds"], line1: stage,
                line2: `${a.code} vs ${h.code}`, blurb: `${a.name} face ${h.name} in ${where} at ${t}.` });
        }
    });


    // De-dupe within the SAME format (a signing and its source news shouldn't
    // double up), but keep every distinct (template × format) variant.
    const seen = new Set();
    const deduped = items.filter((it) => {
        // Each distinct contract row (unique id) gets its own card per format, so
        // the Stories contract count matches the Contract History page 1:1.
        // Every play-by-play event (pbp-*) is inherently unique per (event × game),
        // so it keys off its own eventId — otherwise a player with several events
        // in one game (multiple shots / faceoffs, or a goal PLUS faceoffs) would
        // collapse to a SINGLE card and the recap would drop most toggled events.
        const eid = String(it.eventId || "");
        const base = (eid.startsWith("contract-") || eid.startsWith("pbp-"))
            ? `${it.eventId}|${it.orientation}`
            : `${it.date}|${it.type}|${it.orientation}|${it.s?.line2 || it.s?.subject?.id || it.s?._teamId || it.tpl.v1Key || it.tpl.key}`;
        // Per-game items (result, three stars, standings, GD, PBP) must stay
        // DISTINCT per game — otherwise on a multi-game day the team-subject cards
        // (which share date|type|v1Key) collapse and only the FIRST game keeps its
        // result/three-stars. Scope those by their game id.
        const k = it.s?._gameId ? `${base}|${it.s._gameId}` : base;
        if (seen.has(k)) return false; seen.add(k); return true;
    });

    // Open on the MOST RECENT season that has ANY real story (games, trades,
    // signings, moves) — not just game recaps — so we land near today instead
    // of the inaugural season years ago.
    const realSeasons = Array.from(new Set(deduped.map((i) => i.season).filter(Boolean)));
    const gameSeasons = Array.from(new Set(deduped.filter((i) => i._isGame).map((i) => i.season)));
    const defaultSeason = [...realSeasons].sort((a, b) => b.localeCompare(a))[0] || "";
    // Anchor on the LATEST day of that season so we open on recent activity.
    const seasonDates = deduped.filter((i) => i.season === defaultSeason).map((i) => i.date).sort();
    const allRealDates = Array.from(new Set(deduped.map((i) => i.date))).sort();
    const defaultAnchor = seasonDates[seasonDates.length - 1] || allRealDates[allRealDates.length - 1] || null;

    // NO showcase/demo injection — the public feed shows ONLY real, linked events.
    // (Template previews live in the Creator's Template Manager, built directly
    // from resolveStory — they were never sourced from here.)
    const all = deduped;
    const dates = Array.from(new Set(all.map((i) => i.date))).sort();
    const seasons = Array.from(new Set(all.map((i) => i.season).filter(Boolean)));

    return { items: all, firstDate: dates[0] || null, lastDate: dates[dates.length - 1] || null, seasons, defaultSeason, defaultAnchor, gameSeasons };
}
